import { useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { ApiError, get, qs } from "@/lib/api";
import { S } from "@/lib/strings";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Skeleton } from "@/components/ui/Skeleton";
import type { DeviceSummary, InstalledApp, Page } from "@/types/fleet";

/** How many online devices to union app lists from when none is chosen. */
const SAMPLE_DEVICES = 3;

/**
 * Picks the packages a task is allowed to drive (`TaskSpec.allowedPackages`).
 *
 * The list comes from `GET /api/v1/devices/{id}/apps`, which issues a live
 * `app.list` to the phone (server-cached an hour). With no device chosen there
 * is no fleet-wide app endpoint in the contract, so this unions the lists of a
 * few online devices — bounded at three, because each one is a round trip to a
 * handset and a task's allowlist does not get more correct by asking the
 * eighty-seventh phone.
 *
 * If none of that works — every device offline, endpoint missing — it degrades
 * to free-text package entry rather than blocking the field. An empty allowlist
 * means "any package", which is a decision the operator should make deliberately,
 * not one they fall into because a picker failed to load.
 */
export function AppPicker({
  value,
  onChange,
  deviceId,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  /** Sample this device's installed apps; null unions a few online devices. */
  deviceId?: string | null;
  disabled?: boolean;
}) {
  const [term, setTerm] = useState("");
  const [manual, setManual] = useState("");

  // Only used when no device is pinned: a handful of online devices to sample.
  const sample = useQuery<Page<DeviceSummary>, ApiError>({
    queryKey: ["apppicker", "sample"],
    queryFn: () =>
      get<Page<DeviceSummary>>(`/devices${qs({ health: "online", limit: SAMPLE_DEVICES })}`),
    enabled: !deviceId,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const ids = deviceId
    ? [deviceId]
    : (sample.data?.items ?? []).slice(0, SAMPLE_DEVICES).map((d) => d.id);

  const appQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["device", id, "apps"],
      queryFn: () => get<{ apps: InstalledApp[] }>(`/devices/${id}/apps`),
      staleTime: 60 * 60_000,
      retry: false,
    })),
  });

  const loading = (!deviceId && sample.isPending) || appQueries.some((q) => q.isPending);
  const anySucceeded = appQueries.some((q) => q.isSuccess);

  const apps = useMemo(() => {
    const byPackage = new Map<string, InstalledApp>();
    for (const q of appQueries) {
      for (const a of q.data?.apps ?? []) if (!byPackage.has(a.package)) byPackage.set(a.package, a);
    }
    // Anything already selected must stay visible even if no sampled device has
    // it installed — otherwise editing a task silently drops packages.
    for (const p of value) if (!byPackage.has(p)) byPackage.set(p, { package: p, label: p });
    return [...byPackage.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [appQueries, value]);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return apps;
    return apps.filter(
      (a) => a.label.toLowerCase().includes(needle) || a.package.toLowerCase().includes(needle),
    );
  }, [apps, term]);

  const toggle = (pkg: string) =>
    onChange(value.includes(pkg) ? value.filter((p) => p !== pkg) : [...value, pkg]);

  const addManual = () => {
    const pkg = manual.trim();
    if (!pkg || value.includes(pkg)) return;
    onChange([...value, pkg]);
    setManual("");
  };

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-[11px] text-[var(--color-fg-faint)]">{S.allowedAppsAny}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {value.map((pkg) => {
            const app = apps.find((a) => a.package === pkg);
            return (
              <span
                key={pkg}
                className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] bg-[rgba(255,255,255,0.04)] py-0.5 pl-2 pr-1 text-[11px]"
              >
                <span className="text-[var(--color-fg)]">{app?.label ?? pkg}</span>
                {app && app.label !== pkg && (
                  <span className="font-mono text-[10px] text-[var(--color-fg-faint)]">{pkg}</span>
                )}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => toggle(pkg)}
                  aria-label={`${S.delete} ${pkg}`}
                  className="text-[var(--color-fg-subtle)] hover:text-[#fca5a5] focus-visible:outline-none"
                >
                  <X size={11} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {loading && <Skeleton className="h-32 w-full" />}

      {!loading && anySucceeded && (
        <div className="rounded-md border border-[var(--color-border)]">
          <div className="flex items-center gap-1.5 border-b border-[var(--color-border-subtle)] px-2 py-1.5">
            <Search size={12} className="text-[var(--color-fg-subtle)]" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={S.pickApps}
              disabled={disabled}
              className="w-full bg-transparent text-[12px] placeholder:text-[var(--color-fg-faint)] focus:outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="px-2 py-3 text-center text-[11px] text-[var(--color-fg-faint)]">
                沒有符合的 App。
              </div>
            )}
            {filtered.map((a) => (
              <label
                key={a.package}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[12px]",
                  "hover:bg-[rgba(255,255,255,0.04)]",
                )}
              >
                <Checkbox
                  checked={value.includes(a.package)}
                  onCheckedChange={() => toggle(a.package)}
                  disabled={disabled}
                  ariaLabel={a.label}
                />
                <span className="truncate text-[var(--color-fg)]">{a.label}</span>
                <span className="ml-auto truncate pl-2 font-mono text-[10px] text-[var(--color-fg-faint)]">
                  {a.package}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {!loading && !anySucceeded && (
        <p className="text-[11px] text-[var(--color-fg-faint)]">
          無法從裝置取得 App 清單（裝置離線或未回應）。可以直接輸入 package name。
        </p>
      )}

      <div className="flex items-center gap-1.5">
        <Input
          value={manual}
          disabled={disabled}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addManual();
            }
          }}
          placeholder="com.example.app"
          className="max-w-[260px] font-mono text-[12px]"
        />
        <Button size="sm" variant="default" onClick={addManual} disabled={disabled || !manual.trim()}>
          <Plus size={12} />
          {S.add}
        </Button>
      </div>
    </div>
  );
}
